"use client";

/**
 * 모바일 pull-to-refresh — 화면 최상단에서 아래로 당겨서 새로고침.
 * 2026-05-04 — 사용자 요청. 글로벌 root layout 에 적용 (모든 페이지 자동).
 *
 * 동작:
 * - window.scrollY === 0 일 때만 활성화 (페이지 중간에서 안 작동)
 * - 60px 이상 당기면 활성, 놓으면 window.location.reload() (브라우저 새로고침과 동일)
 * - 100px 이상 당기면 강조 표시
 *
 * 2026-05-04 fix: router.refresh() → window.location.reload()
 *   - router.refresh() 는 RSC 만 다시 fetch, client component (useEffect 데이터) 는 안 트리거
 *   - 사용자 보고: 공급처 페이지 "업체 안 바뀜" — client component fetch 안 됨
 *   - window.location.reload() 는 브라우저 새로고침과 100% 동일 → 모든 페이지 자동 작동
 */

import { useEffect, useState } from "react";

const TRIGGER_DISTANCE = 60;
const MAX_DISTANCE = 100;

export default function PullToRefresh({ children }: { children: React.ReactNode }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let startY = 0;
    let isPulling = false;
    let currentDist = 0;

    const onTouchStart = (e: TouchEvent) => {
      if (window.scrollY > 5) return;   // 최상단만
      startY = e.touches[0].clientY;
      isPulling = true;
      currentDist = 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isPulling) return;
      const cy = e.touches[0].clientY;
      const dist = Math.max(0, cy - startY);
      if (dist > 0) {
        currentDist = Math.min(dist * 0.5, MAX_DISTANCE); // damping
        setPullDistance(currentDist);
      }
    };

    const onTouchEnd = () => {
      if (!isPulling) return;
      isPulling = false;
      if (currentDist >= TRIGGER_DISTANCE) {
        setRefreshing(true);
        setPullDistance(MAX_DISTANCE);
        // 브라우저 새로고침 — RSC + Client Component 모두 새로 fetch (확실)
        window.location.reload();
      } else {
        setPullDistance(0);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  const showIndicator = pullDistance > 5 || refreshing;
  const opacity = Math.min(1, pullDistance / TRIGGER_DISTANCE);
  const isReady = pullDistance >= TRIGGER_DISTANCE;

  return (
    <>
      {showIndicator && (
        <div
          className="fixed top-0 left-0 right-0 flex justify-center pt-3 pointer-events-none z-[100]"
          style={{
            transform: `translateY(${Math.max(0, pullDistance - 30)}px)`,
            opacity,
            transition: refreshing ? "opacity 0.3s" : undefined,
          }}
        >
          <div className="bg-bg/95 backdrop-blur-sm border border-border rounded-full px-3 py-1.5 text-2xs text-text-sub flex items-center gap-1.5">
            {refreshing ? (
              <>
                <span className="inline-block w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span>새로고침 중…</span>
              </>
            ) : isReady ? (
              <span className="text-primary font-semibold">↑ 놓으면 새로고침</span>
            ) : (
              <span>↓ 당겨서 새로고침</span>
            )}
          </div>
        </div>
      )}
      {children}
    </>
  );
}
